// @flow
import { get, post, put, patch, del, getDownload } from '../utils/api';
import { withVoucherTracking } from '../utils/hudVoucherTracking';

const buildQueryStringWithRepeatedKeys = (params: Object = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;

    if (Array.isArray(value)) {
      value
        .filter((v) => v !== undefined && v !== null && v !== '')
        .forEach((v) => searchParams.append(key, String(v)));
      return;
    }

    searchParams.append(key, String(value));
  });

  const qs = searchParams.toString();
  return qs ? `?${qs}` : '';
};

/**
 * Will manage read operations for HUD vouchers and related resources.
 */
export default class HudVoucherService {
  getHudVoucherById(
    organizationId: string,
    propertyId: string,
    hudVoucherId: string,
    options?: Object,
  ): any {
    return get(
      `/${organizationId}/${propertyId}/hud-vouchers/${hudVoucherId}`,
      options,
    );
  }

  getHudVoucherStats(
    organizationId: string,
    propertyId: string,
    hudVoucherId: string,
    options?: Object,
  ): any {
    return get(
      `/${organizationId}/${propertyId}/hud-vouchers/${hudVoucherId}/stats`,
      options,
    );
  }

  getHudVoucherRegularAssistance(
    organizationId: string,
    propertyId: string,
    hudVoucherId: string,
    options?: Object,
  ): any {
    return get(
      `/${organizationId}/${propertyId}/hud-vouchers/${hudVoucherId}/regular-assistance`,
      options,
    );
  }

  getHudVoucherTenantEvents(
    organizationId: string,
    propertyId: string,
    hudVoucherId: string,
    params?: Object,
    options?: Object,
  ): any {
    const queryParams = buildQueryStringWithRepeatedKeys({
      pagination: true,
      ...(params ?? {}),
    });
    return get(
      `/${organizationId}/${propertyId}/hud-vouchers/${hudVoucherId}/tenant-events${queryParams}`,
      options,
    );
  }

  moveTenantEventToNextVoucher(
    organizationId: string,
    propertyId: string,
    hudVoucherId: string,
    tenantEventId: string,
    options?: Object,
  ): any {
    const base = `/${organizationId}/${propertyId}/hud-vouchers/${hudVoucherId}/tenant-events`;
    const path = `${base}/${tenantEventId}/move-to-next-voucher`;
    return withVoucherTracking(
      'tenant_event_moved',
      'POST /hud-vouchers/{hudVoucherId}/tenant-events/{tenantEventId}/move-to-next-voucher',
      () => post(path, undefined, options),
    );
  }

  getRepaymentAgreements(
    organizationId: string,
    propertyId: string,
    voucherId: string,
    options?: Object,
  ): any {
    return get(
      `/${organizationId}/${propertyId}/hud-vouchers/${voucherId}/repayment-agreements`,
      options,
    );
  }

  getHudVoucherContractUnits(
    organizationId: string,
    propertyId: string,
    voucherId: string,
    options?: Object,
  ): any {
    return get(
      `/${organizationId}/${propertyId}/hud-vouchers/${voucherId}/contract-units`,
      options,
    );
  }

  modifyRepaymentAgreement(
    organizationId: string,
    propertyId: string,
    voucherId: string,
    voucherRepaymentRowId: string,
    payload: Object,
    options?: Object,
  ): any {
    return withVoucherTracking(
      'repayment_agreement_updated',
      'PATCH /hud-vouchers/{voucherId}/repayment-agreements/{voucherRepaymentRowId}',
      () =>
        patch(
          `/${organizationId}/${propertyId}/hud-vouchers/${voucherId}/repayment-agreements/${voucherRepaymentRowId}`,
          JSON.stringify(payload),
          options,
        ),
    );
  }

  createRepaymentAgreement(
    organizationId: string,
    propertyId: string,
    voucherId: string,
    payload: Object,
    options?: Object,
  ): any {
    return withVoucherTracking(
      'repayment_agreement_created',
      'POST /hud-vouchers/{voucherId}/repayment-agreements',
      () =>
        post(
          `/${organizationId}/${propertyId}/hud-vouchers/${voucherId}/repayment-agreements`,
          JSON.stringify(payload),
          options,
        ),
    );
  }

  deleteRepaymentAgreement(
    organizationId: string,
    propertyId: string,
    voucherId: string,
    repaymentAgreementId: string,
    options?: Object,
  ): any {
    return withVoucherTracking(
      'repayment_agreement_deleted',
      'DELETE /hud-vouchers/{voucherId}/repayment-agreements/{repaymentAgreementId}',
      () =>
        del(
          `/${organizationId}/${propertyId}/hud-vouchers/${voucherId}/repayment-agreements/${repaymentAgreementId}`,
          undefined,
          options,
        ),
    );
  }

  updateRepaymentAgreementApprovedAmount(
    organizationId: string,
    propertyId: string,
    voucherId: string,
    voucherRepaymentRowId: string,
    approvedAmount: number,
    options?: Object,
  ): any {
    return withVoucherTracking(
      'repayment_agreement_amount_updated',
      'PATCH /hud-vouchers/{voucherId}/repayment-agreements/{voucherRepaymentRowId}/approved-amount',
      () =>
        patch(
          [
            `/${organizationId}/${propertyId}/hud-vouchers/${voucherId}/repayment-agreements`,
            `/${voucherRepaymentRowId}/approved-amount`,
          ].join(''),
          JSON.stringify({ approvedAmount }),
          options,
        ),
    );
  }

  deleteSpecialClaim(
    organizationId: string,
    propertyId: string,
    voucherId: string,
    specialClaimId: string,
    options?: Object,
  ): any {
    return withVoucherTracking(
      'special_claim_deleted',
      'DELETE /hud-vouchers/{voucherId}/special-claims/{specialClaimId}',
      () =>
        del(
          `/${organizationId}/${propertyId}/hud-vouchers/${voucherId}/special-claims/${specialClaimId}`,
          undefined,
          options,
        ),
    );
  }

  downloadHud52670(
    organizationId: string,
    propertyId: string,
    hudVoucherId: string,
    fileName: string,
    options?: Object,
  ): any {
    // Send the browser timezone so the PDF renders DATEONLY fields the same
    // way the UI does (new Date(...) in the browser's local zone).
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const query = browserTimezone
      ? `?timezone=${encodeURIComponent(browserTimezone)}`
      : '';
    return getDownload(
      `/${organizationId}/${propertyId}/hud-vouchers/${hudVoucherId}/52670-pdf${query}`,
      fileName,
      options,
    );
  }

  saveExecutionData(
    organizationId: string,
    propertyId: string,
    hudVoucherId: string,
    payload: Object,
    options?: Object,
  ): any {
    return withVoucherTracking(
      'execution_saved',
      'PUT /hud-vouchers/{hudVoucherId}/execution',
      () =>
        put(
          `/${organizationId}/${propertyId}/hud-vouchers/${hudVoucherId}/execution`,
          JSON.stringify(payload),
          options,
        ),
    );
  }

  undoSubmittedVoucherChanges(
    organizationId: string,
    propertyId: string,
    voucherId: string,
    options?: Object,
  ): any {
    return withVoucherTracking(
      'submitted_changes_undone',
      'POST /hud-vouchers/{hudVoucherId}/undo-submitted-changes',
      () =>
        post(
          `/${organizationId}/${propertyId}/hud-vouchers/${voucherId}/undo-submitted-changes`,
          undefined,
          options,
        ),
    );
  }

  deriveRegularAssistance(
    organizationId: string,
    propertyId: string,
    hudVoucherId: string,
    options?: Object,
  ): any {
    return post(
      `/${organizationId}/${propertyId}/hud-vouchers/${hudVoucherId}/regular-assistance/derive`,
      undefined,
      options,
    );
  }

  saveApprovalData(
    organizationId: string,
    propertyId: string,
    hudVoucherId: string,
    payload: Object,
    options?: Object,
  ): any {
    return withVoucherTracking(
      'approval_saved',
      'PUT /hud-vouchers/{hudVoucherId}/approval',
      () =>
        put(
          `/${organizationId}/${propertyId}/hud-vouchers/${hudVoucherId}/approval`,
          JSON.stringify(payload),
          options,
        ),
    );
  }

  reconcileRegularAssistance(
    organizationId: string,
    propertyId: string,
    payload?: Object,
    options?: Object,
  ): any {
    return post(
      `/${organizationId}/${propertyId}/hud-vouchers/regular-assistance/reconcile`,
      payload ? JSON.stringify(payload) : undefined,
      options,
    );
  }

  finalizeApproval(
    organizationId: string,
    propertyId: string,
    hudVoucherId: string,
    payload: Object,
    options?: Object,
  ): any {
    return withVoucherTracking(
      'approval_finalized',
      'POST /hud-vouchers/{hudVoucherId}/approve',
      () =>
        post(
          `/${organizationId}/${propertyId}/hud-vouchers/${hudVoucherId}/approve`,
          JSON.stringify(payload),
          options,
        ),
    );
  }

  markVoucherAsPaid(
    organizationId: string,
    propertyId: string,
    hudVoucherId: string,
    options?: Object,
  ): Promise<Object> {
    return withVoucherTracking(
      'marked_paid',
      'POST /hud-vouchers/{hudVoucherId}/mark-as-paid',
      () =>
        post(
          `/${organizationId}/${propertyId}/hud-vouchers/${hudVoucherId}/mark-as-paid`,
          undefined,
          options,
        ),
    );
  }
}
